import { hasHassFlow } from '../hass/pageHass.js';
import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';

import { Group } from 'konva/lib/Group';
import { splitColorOpa, opaWireKey } from '../utils/color';
import { mergeConfigWithTheme, setNodeProperties, getActiveTheme } from './properties/ConfigManager';
import { isSchemaPropertyVisible, schemaPropertyVisibilityDependencies } from './properties/v2/propertyVisibility.js';
import { BG_GRADIENT_STYLE } from './properties/v2/shared.js';
import { resolveGradientAxis } from '../draw/backgroundGradient';
import { servicesLookup } from '../utils/lookupTables';
import HaspObject from './HaspObject';
import CustomRect from '../draw/CustomRect';
import LvglShadowShape from '../draw/LvglShadowShape';
import { moveObjectBackward } from './objectLayerOrder.js';
import { applyObjChildClip, withoutObjChildClip } from './objClipping.js';
// The border_side/border_opa bitmask constants live with the geometry that
// consumes them (src/draw/borderGeometry.ts) — one registry, not two.
import { BORDER_OPA_COVER, BORDER_SIDE_FULL } from '../draw/borderGeometry';

export default class GroupObject extends Group {
  scaledX = 1;
  scaledY = 1;
  // enabled = true;
  expanded = false;
  constructor(config) {
    // config.show_id ??= false;
    config.comment ??= '';

    mergeConfigWithTheme(config);

    super(config);
    const uuid = config._uuid ?? uuidv4();
    this.id(uuid);
    // this.uuid = uuid;

    setNodeProperties(this, config);
    if (this.hidden) this.visible(false);

    // Keep the LVGL child mask on the obj for hit-testing and compatibility.
    // Scene drawing handles the mask in _drawChildren() so the parent rect and
    // its outline can be placed on opposite sides of the clipped child pass.
    applyObjChildClip(this);
    if (this.obj === 'obj') {
      // Covers widget-specific direct size() calls as well as the common
      // Transformer/setNewSize path.
      this.on('widthChange heightChange', () => applyObjChildClip(this));
    }

    let clipOptions = {};
    if (this.isType('obj') || this.isType('arc') || this.isType('linemeter')) {
      clipOptions = {
        clipX: 0,
        clipY: 0,
        clipWidth: config.width,
        clipHeight: config.height,
      };
    }

    // Split 8-char hex (#rrggbbaa) into colour + opacity before passing to Konva.
    // Canvas 2D fillStyle doesn't reliably support the #RRGGBBAA form in all versions.
    const bgDefault = this.hasStyleProperty('main', 'bg_color') ? '#000000ff' : '#00000000';
    const bgSplit = splitColorOpa(this.styleProp('main', 'bg_color') ?? bgDefault);
    const borderRaw = this.styleProp('main', 'border_color');
    const borderSplit = splitColorOpa(borderRaw ?? '#000000ff');
    const borderHasAlpha = typeof borderRaw === 'string' && borderRaw.length > 7;
    this.rect = new CustomRect({
      id: uuidv4(),
      x: 0,
      y: 0,
      width: config.width,
      height: config.height,
      clipOptions,
      fill: bgSplit.color,
      stroke: borderSplit.color,
      borderWidth: this.styleProp('main', 'border_width'),
      // Unset resolves to LVGL's own defaults: all four sides, fully opaque.
      borderSide: this.styleProp('main', 'border_side') ?? BORDER_SIDE_FULL,
      borderOpa: borderHasAlpha
        ? Math.round(borderSplit.opacity * 255)
        : this.styleProp('main', 'border_opa') ?? BORDER_OPA_COVER,
      draggable: false,
      name: 'rect',
      obj: this.obj,
      selectable: true,
      cornerRadius: this.styleProp('main', 'radius'),
      // An obj draws its outline in a separate post-children pass. Other
      // object types keep CustomRect's normal inline outline rendering.
      drawOutline: this.obj !== 'obj',
      listening: true,
      opacity: bgSplit.opacity,
    });
    if (this.obj === 'obj') {
      this.outlineRect = new CustomRect({
        id: uuidv4(),
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
        outlineOnly: true,
        fill: null,
        stroke: null,
        listening: false,
        draggable: false,
        name: 'outline',
        obj: 'outline',
        selectable: false,
      });
      const syncOutlineSize = () => this.outlineRect.size(this.rect.size());
      this.rect.on('widthChange heightChange', syncOutlineSize);
    }
    if (this.hasStyleProperty('main', 'bg_color')) this.applyMainBackgroundStyle();

    // One shadow shape per object, added BEFORE the rect so it paints behind
    // the background and border. Every widget inherits it from here; there is
    // no per-widget shadow code anywhere.
    //
    // It takes its box from the main rect rather than owning a copy: several
    // widgets resize `this.rect` directly instead of going through setNewSize()
    // (HaspImage._onImageReady, HaspMsgboxButton.updateButton), and a lazily
    // read box stays correct for all of them with no per-widget sync calls.
    this.shadow = new LvglShadowShape({
      x: 0,
      y: 0,
      sizeProvider: () => this.rect.size(),
      name: 'shadow',
      obj: this.obj,
      selectable: false,
    });

    this.on('transformstart', () => {
      this.transformStart();
      this.getLayer().transformStart(this);
    });

    //set newsize for rect and set scale to 1
    this.on('transform', (e) => {
      this.scaledX = this.scaleX();
      this.scaledY = this.scaleY();
      const scaleX = this.scaleX();
      const scaleY = this.scaleY();
      const width = this.width();
      const height = this.height();

      const x = Math.round(this.x());
      const y = Math.round(this.y());
      const transformedWidth = width * scaleX;
      const transformedHeight = height * scaleY;

      if (transformedWidth < 0 || transformedHeight < 0) return;

      const layer = this.getLayer();
      const ratioGeometry = layer?.constrainTransformGeometry?.(this, {
        x: this.x(),
        y: this.y(),
        width: transformedWidth,
        height: transformedHeight,
      });

      let scaledWidth;
      let scaledHeight;
      if (ratioGeometry) {
        this.x(ratioGeometry.x);
        this.y(ratioGeometry.y);
        scaledWidth = ratioGeometry.width;
        scaledHeight = ratioGeometry.height;
      } else {
        this.x(x);
        this.y(y);

        scaledWidth = Math.round(transformedWidth);
        scaledHeight = Math.round(transformedHeight);
        if (scaledWidth < this.min_width) scaledWidth = this.min_width;
        if (scaledHeight < this.min_height) scaledHeight = this.min_height;
      }


      const newSize = { width: scaledWidth, height: scaledHeight };
      if (this.isType('obj') || this.isType('arc') || this.isType('linemeter') || this.isType('tabview')) {
        // this.clipWidth(scaledWidth);
        // this.clipHeight(scaledHeight);
      }
      this.rect.size(newSize);
      this.size(newSize);
      applyObjChildClip(this);


      this.scaleX(1);
      this.scaleY(1);

      // Connections are page-level siblings of component children, so they do
      // not receive the parent's Konva transform. Re-route only connections
      // whose two badge endpoints are inside this group after the resize.
      this.getLayer()?.refreshBadgeConnectionsForParent?.(this);

      // this.getLayer().transform(this);
      this.idTextObj.moveToTop();
      this.getLayer().transform(this);
    });

    //this is where transformation start
    this.on('transformend', () => {
      this.getLayer().transformEnd(this);
      this.transformEnd();
      this.idTextObj.moveToTop();
    });

    this.on('showid', (ev) => {
      this.showIdHandler(ev);
    });

    this.on('update-ratio', () => {
      this.getLayer().updateTransformer();
    });

    this.add(this.shadow);
    this.add(this.rect);
    if (this.outlineRect) this.add(this.outlineRect);
    this.applyShadowStyle();
    this.applyOutlineStyle();
    this.addIdText(config);
    this.draggable(true);

    // For clipping containers, override getClientRect so the Transformer
    // positions its handles at the rect bounds, not the children's bounds.
    // This ensures correct resize behaviour when children extend outside the obj.
    // Also apply to all object types to ensure CustomRect's exact bounds are used
    if (this.isType('obj') || this.isType('tabview') || this.rect instanceof CustomRect) {
      this.getClientRect = (config) => {
        const local = { x: 0, y: 0, width: this.rect.width(), height: this.rect.height() };
        if (config && config.skipTransform) return local;
        return this._transformedRect(local, config && config.relativeTo);
      };
    }

    // Override drawHit to skip the clip region so children extending outside the
    // visual clip boundary (obj, tabview, page) remain draggable when selected from the tree.
    if (this.isType('obj') || this.isType('tabview') || this.isType('page')) {
      const _origDrawHit = this.drawHit.bind(this);
      this.drawHit = (can, top) => {
        if (this.isType('obj')) {
          return withoutObjChildClip(this, () => _origDrawHit(can, top));
        }
        const savedW = this.clipWidth();
        const savedH = this.clipHeight();
        const result = _origDrawHit(can, top);
        this.clipWidth(savedW);
        this.clipHeight(savedH);
        return result;
      };
    }
  }

  /**
   * Render obj in LVGL order without changing the public Konva tree:
   * parent background/border, clipped descendants, then the parent's outline.
   *
   * Keeping descendants as direct children is important because the Designer
   * uses that tree for selection, export and parent/child bookkeeping. The
   * normal Container implementation clips the whole Group, so it cannot put
   * the outline outside that clip; this small scene-only pass does.
   */
  _drawChildren(drawMethod, canvas, top, bufferCanvas) {
    if (this.obj !== 'obj' || drawMethod !== 'drawScene') {
      return super._drawChildren(drawMethod, canvas, top, bufferCanvas);
    }

    const context = canvas && canvas.getContext();
    const children = this.getChildren().slice();
    const selfCache = top === this;
    const hasComposition = context && !selfCache
      && this.globalCompositeOperation() !== 'source-over'
      && drawMethod === 'drawScene';

    if (hasComposition) {
      context.save();
      context._applyGlobalCompositeOperation(this);
    }

    const draw = (child) => child[drawMethod](canvas, top, bufferCanvas);
    const drawParent = (child) => child === this.rect;
    const drawShadow = (child) => child === this.shadow;
    const drawOutline = (child) => child === this.outlineRect;
    const drawId = (child) => child === this.idTextObj;

    const drawClipped = (nodes) => {
      if (!context) {
        nodes.forEach(draw);
        return;
      }

      context.save();
      const transform = this.getAbsoluteTransform(top);
      let matrix = transform.getMatrix();
      context.transform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
      context.beginPath();
      context.rect(0, 0, this.width(), this.height());
      context.clip();
      matrix = transform.copy().invert().getMatrix();
      context.transform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
      nodes.forEach(draw);
      context.restore();
    };

    // Preserve the existing obj behaviour for shadows: the shadow is part of
    // the parent visual, but its overflow remains clipped to the obj bounds.
    drawClipped(children.filter(drawShadow));

    // CustomRect contains the complete parent background/border pass. For obj,
    // its inline outline is disabled (see constructor).
    children.filter(drawParent).forEach(draw);

    const clippedChildren = children.filter(child =>
      !drawParent(child) && !drawShadow(child) && !drawOutline(child) && !drawId(child));

    drawClipped(clippedChildren);

    // The outline is intentionally outside the child clip. Keep the ID label
    // last, matching the existing Designer overlay behaviour.
    children.filter(drawOutline).forEach(draw);
    children.filter(drawId).forEach(draw);

    if (hasComposition) context.restore();
  }

  addIdText(config) {
    this.idTextObj = new Konva.Text({
      x: 2,
      y: 2,
      verticalAlign: 'left',
      text: this.haspid,
      fill: '#fcf803',
      stroke: 'black',
      fontFamily: 'Roboto Condensed',
      fontStyle: 'bold',
      fontSize: 18,
      strokeWidth: 0.5,
      listening: false,
      visible: config.show_id,
      obj: 'idtxt',
    });

    this.add(this.idTextObj);
    this.idTextObj.moveToTop();
    // this.idTextObj.hide();
  }

  updateId(id) {
    this.haspid = id;
    this.idTextObj.setAttr('text', id);
    this.idTextObj.moveToTop();
    // this.getLayer().layerCallback(this);
  }

  showIdHandler(e) {
    if (e.evt.showId) {
      this.idTextObj.show();
      this.idTextObj.moveToTop();
    } else {
      this.idTextObj.hide();
    }

    const event = new Event('showid');
    event.showId = e.evt.showId;
    this.getChildren().forEach((element) => {
      if (element instanceof HaspObject) {
        element.dispatchEvent(event);
      }
    });
  }

  transformStart() { }

  transformEnd() {
    this.setObjectSize();
  }

    getNextId() {
      // this.top_haspid++;
      return this.top_haspid++;
    }

  layerMoveDown() {
    moveObjectBackward(this, sibling => sibling instanceof HaspObject);
  }

  setNewSize(size) {
    this.rect.size(size);
    this.size(size);
    applyObjChildClip(this);
  }

  /**
   * Push main's resolved shadow style onto the shared LvglShadowShape.
   *
   * Every value comes straight out of the StyleResolver cascade (theme ->
   * local -> state, with the active state/preview applied), so state-specific
   * shadow overrides and live theme switches need no extra code here. Unset
   * properties stay `undefined` and LVGL's own defaults are applied inside
   * shadowGeometry.ts — no defaults are duplicated at this level.
   */
  applyShadowStyle() {
    if (!this.shadow) return;
    const rawColor = this.styleProp('main', 'shadow_color');
    const hasAlpha = typeof rawColor === 'string' && rawColor.length > 7;
    this.shadow.setShadowParams({
      shadowWidth: this.styleProp('main', 'shadow_width'),
      shadowSpread: this.styleProp('main', 'shadow_spread'),
      ofsX: this.styleProp('main', 'shadow_ofs_x'),
      ofsY: this.styleProp('main', 'shadow_ofs_y'),
      color: rawColor,
      opa: hasAlpha ? 255 : this.styleProp('main', 'shadow_opa'),
      radius: this.styleProp('main', 'radius'),
    });
  }

  /**
   * Push main's resolved outline style onto the shared CustomRect.
   *
   * Same contract as applyShadowStyle() above: every value comes straight out
   * of the StyleResolver cascade (schema default -> theme -> local -> state,
   * with the active state/preview applied), unset properties stay `undefined`
   * and LVGL's own defaults are applied inside outlineGeometry.ts. `radius` is
   * pushed with them because the outline's corners are derived from the
   * object's radius — the two must never be updated apart.
   *
   * Widgets whose schema declares no outline properties resolve every value to
   * `undefined`, which renders nothing; no widget-specific check is needed.
   *
   * outline_color is hex8 in the editor (its alpha IS outline_opa, same deal as
   * bg_color/bg_opa), so it is split here — Canvas can't be relied on to accept
   * the #rrggbbaa form. A plain 6-hex value (a theme default, or anything the
   * cascade supplies without alpha) leaves the alpha unstated, in which case
   * the separately resolved outline_opa applies; if that is unset too,
   * outlineGeometry falls back to LVGL's LV_OPA_COVER.
   */
  applyOutlineStyle() {
    if (!this.rect?.setOutlineParams) return;
    const rawColor = this.styleProp('main', 'outline_color');
    const hasAlpha = typeof rawColor === 'string' && rawColor.length > 7;
    const split = splitColorOpa(rawColor ?? '#000000');
    const params = {
      outlineWidth: this.styleProp('main', 'outline_width'),
      outlineColor: rawColor === undefined ? undefined : split.color,
      outlineOpa: hasAlpha
        ? Math.round(split.opacity * 255)
        : this.styleProp('main', 'outline_opa'),
      outlinePad: this.styleProp('main', 'outline_pad'),
      cornerRadius: this.styleProp('main', 'radius'),
    };
    this.rect.setOutlineParams(params);
    this.outlineRect?.setOutlineParams(params);
  }

  /**
   * Which resize handles the Transformer should offer for this object.
   * One of: 'auto' | 'none' | 'keep_ratio' | 'horizontal' | 'vertical' | 'both'.
   * Subclasses override to react to their own state (e.g. HaspDropdown
   * restricts to 'horizontal' while collapsed).
   */
  getResizeMode() {
    if (this.size_kind === 'auto') return 'auto';
    if (this.stretch_mode === 'keep_ratio') return 'keep_ratio';
    if (this.stretch_mode === 'h_stretch') return 'horizontal';
    if (this.stretch_mode === 'v_stretch') return 'vertical';
    if (this.stretch_mode === 'none') return 'none';
    return 'both';
  }

  setFill(color) {
    if (this.rect !== undefined) this.rect.fill(color);
  }

  setObjectSize() {
    if (this.isType('obj') || this.isType('arc') || this.isType('linemeter')) {
      // this.clipWidth(this.rect.width());
      // this.clipHeight(this.rect.height());
    }
    // this.size(size);
    // this.rect.size(size);
  }

  setBorderColor(_color) {
    if (this.rect !== undefined) {
      this.rect.stroke(this.styleProp('main', 'border_color'));
    }
  }

  setHaspId(haspid) {
    this.haspid = haspid;
  }

  /**
   * We set the bg_color to the full rgbo color red green blue opa
   * we load bg_color from config.bg_color and add bg_opa
   * @param {*} hex8color
   */
  setBgColor(_hex8color) {
    const splitColors = splitColorOpa(this.styleProp('main', 'bg_color') ?? '#000000ff');
    this.rect.fill(splitColors.color);
    this.rect.opacity(splitColors.opacity);
    this.adjust();
  }

  setSelected(_selected) {
  }

  hasProperty(property) {
    if (this.schemaPropertyKeys) return this.schemaPropertyKeys.has(property);
    return this.properties?.includes(property) ?? false;
  }

  /**
   * Returns true when the object's v2 schema defines the given LVGL part.
   * Replaces the legacy `hasProperty('has_indicator10')` style checks.
   * Falls back to false when no v2 schema is attached (should not happen in practice).
   */
  hasPart(partName) {
    if (this.schemaV2) return partName in this.schemaV2.parts;
    return false;
  }

  /** Returns true when the given part declares the given style property in this object's v2 schema. */
  hasStyleProperty(partName, propName) {
    return this.schemaV2?.parts?.[partName]?.properties?.[propName] !== undefined;
  }

  /** Returns this object's schema definition for a part (label/order/exportSuffix/properties), or undefined. Centralizes schema.parts access — callers should never navigate schemaV2 directly. */
  getStyleDefinition(partName) {
    return this.schemaV2?.parts?.[partName];
  }

  /** Returns the metadata (type/min/max/label/…) for one part+property pair, or undefined. */
  getStylePropertyDefinition(partName, propName) {
    return this.schemaV2?.parts?.[partName]?.properties?.[propName];
  }

  /** Returns this object's declared part names (e.g. ['main', 'indicator', 'knob']). */
  getPartNames() {
    return Object.keys(this.schemaV2?.parts ?? {});
  }

  /** Returns the metadata (type/min/max/label/…) for one top-level (non-style) property, or undefined. */
  getPropertyDefinition(propName) {
    return this.schemaV2?.properties?.[propName];
  }

  /**
   * Evaluate declarative top-level property visibility metadata.
   *
   * `visibleWhen` is UI-only schema metadata; it never changes the stored
   * object value or the export domain. The supported form is deliberately
   * small and data-only so schemas remain serializable:
   * `{ property: 'type', equals: 3 }` or `{ property: 'type', in: [1, 2] }`.
   */
  isPropertyVisible(propName) {
    return isSchemaPropertyVisible(this, propName);
  }

  /** Return object-property names whose values can change a visibility rule. */
  getPropertyVisibilityDependencies() {
    return schemaPropertyVisibilityDependencies(this.schemaV2);
  }

  /**
   * Top-level properties declared `type: 'resource'`, split by `meta.inline`.
   * By default (or `{ inline: false }`) returns the non-inline ones, which
   * drive the generic resource-property Accordion in the Property Editor.
   * `{ inline: true }` returns the ones rendered inline among the main
   * properties instead (e.g. label.js's `font`, gauge.js's
   * `value_font_resource`) — same enumeration, just the opposite filter, so
   * a new resource property of either kind never requires template changes
   * in the Property Editor.
   */
  getResourceProperties(options = {}) {
    const { inline = false } = options;
    const properties = this.schemaV2?.properties ?? {};
    return Object.entries(properties).filter(([, meta]) => meta.type === 'resource' && !!meta.inline === inline);
  }

  /** Returns true when ANY part declares the given style property — for generic, part-agnostic UI guards (e.g. per-state overrides). */
  hasAnyStyleProperty(propName) {
    if (!this.schemaV2?.parts) return false;
    return Object.values(this.schemaV2.parts).some(partDef => propName in (partDef.properties ?? {}));
  }

  /**
   * Returns true when propName is declared anywhere — top-level OR as a style
   * property of any part. For generic, domain-agnostic UI guards (e.g. the
   * per-state override panel, which isn't part-scoped and doesn't know or
   * care whether a given property happens to be top-level data or a part's
   * style on any particular widget type).
   */
  hasPropertyAnywhere(propName) {
    return this.hasProperty(propName) || this.hasAnyStyleProperty(propName);
  }

  /**
   * The active theme's style slice for THIS object's widget type
   * (e.g. the equivalent of curr_theme['btn']), fetched fresh on every call
   * and handed to the resolver as plain data — never cached on the node,
   * never cached inside the resolver. getActiveTheme() is memoized at the
   * ConfigManager level, so this stays cheap across a whole render pass and
   * automatically reflects a live theme switch with no invalidation code
   * needed here (see StyleResolver.style()'s cache-key-by-theme-reference).
   */
  _activeThemeSlice() {
    return this._themeOverride?.[this.obj] ?? getActiveTheme()?.[this.obj];
  }

  /**
   * Resolved style for a whole part — the preferred, primary style API.
   * Widgets should consume this rather than individual flat properties.
   *
   * `state` is normally omitted — every widget's own redraw/onStyle code
   * calls this with just a part name, and that omitted state resolves to
   * `_previewState` if one is active (see setPreviewState()) or 'default'
   * otherwise. This is what lets a live state preview work with ZERO
   * widget-specific code: every widget already re-reads style()/styleProp()
   * from its onStyle() listeners, so it automatically shows whatever state
   * is currently active. Infrastructure that needs one SPECIFIC state
   * regardless of any active preview (the Property Editor's per-state
   * panels, the exporter) must pass `state` explicitly — including the
   * literal string 'default', which always means true default, never the
   * preview. See v2/StyleResolver.js.
   */
  style(part, state) {
    const effectiveState = state ?? this._previewState ?? 'default';
    return this.styleResolver?.style(part, { theme: this._activeThemeSlice(), state: effectiveState }) ?? {};
  }

  /** Resolved value for a single style property — see style() above for the `state` omission/preview semantics. */
  styleProp(part, prop, state) {
    const effectiveState = state ?? this._previewState ?? 'default';
    return this.styleResolver?.resolveStyleProp(part, prop, { theme: this._activeThemeSlice(), state: effectiveState });
  }

  /**
   * The sole mutation entry point for style properties. The resolver only
   * writes the value and tracks dirtiness (see StyleResolver.setStyleProp) —
   * this node decides what happens as a result, via refreshStyle below.
   */
  setStyle(part, prop, value) {
    const changed = this.styleResolver?.setStyleProp(part, prop, value);
    if (changed) this.refreshStyle(part, prop);
    return changed;
  }

  /** Write without notifying — for transient UI feedback only (e.g. live color-picker drag). See StyleResolver.pokeStyle. */
  pokeStyle(part, prop, value) {
    return this.styleResolver?.pokeStyle(part, prop, value);
  }

  /**
   * The semantic states this widget type supports (e.g.
   * ['checked','pressed',...]), from the schema's `states` array — drives
   * the Property Editor's per-state sub-accordions and the exporter's
   * per-state wire-key generation. Empty for widget types with no state
   * overrides (chart/legend/…).
   */
  getSupportedStates() {
    return this.schemaV2?.states ?? [];
  }

  /** Resolved value for a single style property in an explicit non-default state — for the Property Editor's per-state fields and the exporter. */
  stateStyleProp(part, state, prop) {
    return this.styleResolver?.resolveStyleProp(part, prop, { theme: this._activeThemeSlice(), state });
  }

  /** The sole mutation entry point for a non-default state's style properties. Delegates to setStyle when state is 'default'. */
  setStateStyle(part, state, prop, value) {
    const changed = this.styleResolver?.setStateStyleProp(part, state, prop, value);
    if (changed) this.refreshStyle(part, prop);
    return changed;
  }

  /**
   * True when THIS node explicitly overrides part/prop at the given state
   * (as opposed to inheriting it through the cascade) — lets the Property
   * Editor distinguish "inherited" from "explicit-and-happens-to-match".
   * Defaults to the 'default' state, matching style()/styleProp() above.
   */
  hasExplicitStyle(part, prop, state = 'default') {
    return this.styleResolver?.hasExplicitValue(part, prop, { state }) ?? false;
  }

  /** Deletes an explicit override (default or state-scoped), reverting to whatever the cascade beneath resolves to. */
  resetStateStyle(part, state, prop) {
    const changed = this.styleResolver?.resetStateStyleProp(part, state, prop);
    if (changed) this.refreshStyle(part, prop);
    return changed;
  }

  /**
   * Full cascade trace for a part/prop/state — {value, source, explicit,
   * inherited, redundant}. The sole primitive the export optimizer, schema
   * validator and Property Editor "inherited vs explicit" indicator use to
   * make their decisions; none of them may compare against a default or
   * theme value on their own. See StyleResolver.resolveTrace.
   */
  resolveStyleTrace(part, prop, state = 'default') {
    return this.styleResolver?.resolveTrace(part, prop, { theme: this._activeThemeSlice(), state })
      ?? { value: undefined, source: undefined, explicit: false, inherited: true, redundant: false };
  }

  /**
   * Toggle a part's "keep circle" lock: flips its internal keep_circle flag
   * and snaps radius to 999 (circle) or 5 (rounded rect) accordingly. Works
   * for any part that declares keep_circle — replaces the old per-part
   * (main/indicator/knob) duplicated handlers keyed by wire-format event name.
   */
  toggleKeepCircle(part) {
    const next = !this.style(part).keep_circle;
    this.setStyle(part, 'keep_circle', next);
    this.setStyle(part, 'radius', next ? 999 : 5);
  }

  /**
   * Subscribe to a style property's refresh notification (fired by
   * refreshStyle above). The handler receives no value — always pull the
   * current value via style()/styleProp() inside it. Migrated widgets should
   * use this instead of the legacy this.on(wireKey, ...) pattern, so redraw
   * code never needs to know a property's export suffix.
   */
  onStyle(part, prop, handler) {
    this.on(`style-refresh:${part}:${prop}`, handler);
  }

  /** Unsubscribe a handler previously registered via onStyle() — e.g. a Vue component cleaning up on unmount. */
  offStyle(part, prop, handler) {
    this.off(`style-refresh:${part}:${prop}`, handler);
  }

  /**
   * The notification-only half of a style refresh: fire the semantic
   * style-refresh event carrying no value (consumers always pull fresh state
   * via style()/styleProp(), never from the event payload), special-casing
   * color properties through changeColor() for the main-rect fill/stroke
   * case. Never touches stored data, history, or serialization — it only
   * tells already-registered onStyle() listeners "go re-read your value."
   *
   * This is the ONE implementation both refreshStyle() (below, the
   * persisting write path) and refreshAllStyles() (the generic bulk path
   * used by state preview and, in future, live theme switching) build on —
   * neither duplicates this logic.
   */
  _notifyStyleRefresh(part, prop) {
    const partDef = this.schemaV2?.parts?.[part];
    const meta = partDef?.properties?.[prop];

    if (part === 'main' && prop === 'bg_color') {
      this.changeColor(part, prop, this.styleProp(part, prop));
    } else if (part === 'main' && Object.hasOwn(BG_GRADIENT_STYLE, prop)) {
      this.applyMainBackgroundStyle();
    } else if (meta?.type === 'color') {
      this.changeColor(part, prop, this.styleProp(part, prop));
    }
    this.fire(`style-refresh:${part}:${prop}`);
  }

  /**
   * True when this node's page ancestor is Page 0 — the shared overlay page
   * whose bitmap is recaptured and broadcast to every other page. Used to
   * skip that (expensive) recapture for edits that can't possibly affect it.
   */
  isOnPageZero() {
    let node = this;
    while (node && node.obj !== 'page' && node.obj !== 'template_page') {
      node = node.getParent?.();
    }
    return node?.haspid === 0;
  }

  /**
   * Everything that happens after a style property actually changes:
   * the notification (see _notifyStyleRefresh) plus history/menu/redraw
   * bookkeeping. Deliberately NOT part of StyleResolver: the resolver is a
   * pure style engine (storage, resolution, caching, dirty tracking) and
   * knows nothing about Konva, layers, history or redraw.
   *
   * The label passed to layerCallback (`${part}:${prop}`) is purely
   * diagnostic/history bookkeeping — never a firmware wire key; this file
   * has no notion of OpenHASP serialization.
   */
  refreshStyle(part, prop) {
    this._notifyStyleRefresh(part, prop);

    const layer = this.getLayer?.();
    if (layer) {
      if (!layer._historySuppress) layer.layerCallback(`${part}:${prop}`, this);
      layer.updateObjectMenu(this);
      if (this.isOnPageZero()) layer.updatePageZeroOverlay();
      layer.requestRender();
    }
  }

  /**
   * Generic style refresh — the single mechanism for re-notifying a
   * widget's existing onStyle() listeners that the effective style may have
   * changed, without touching stored data, history, or serialization: fires
   * `style-refresh:<part>:<prop>` for every property this widget's schema
   * declares, across every part, then requests one redraw. No widget-
   * specific code is involved — every widget already reacts to these events
   * via its own onStyle() subscriptions.
   *
   * This is the foundation for:
   *   - state preview (setPreviewState/clearPreviewState below)
   *   - future live theme switching (setActiveTheme -> this -> redraw,
   *     once callers start invoking it after a theme change — see
   *     docs/FOLLOWUP-TODOS.md)
   *   - any other future "reapply resolved styles to an already-
   *     constructed widget" need
   * There is exactly one implementation; do not add a second bulk-refresh
   * path for a new use case — call this one.
   */
  refreshAllStyles() {
    for (const partName of this.getPartNames()) {
      for (const propName of Object.keys(this.getStyleDefinition(partName)?.properties ?? {})) {
        this._notifyStyleRefresh(partName, propName);
      }
    }
    this.getLayer?.()?.requestRender();
  }

  /**
   * Temporarily preview a non-default state's appearance on canvas — a thin
   * UI layer over the existing cascade, not a second state model. Sets the
   * state style()/styleProp() fall back to when a caller omits `state`
   * (i.e. every widget's own redraw/onStyle code), then runs the generic
   * refresh so already-constructed widgets immediately reflect it. Nothing
   * is written to StyleResolver, no history entry is created, and nothing
   * is exported — see PropertiesPanel's StatesPanel.vue for the "Test" UI.
   */
  setPreviewState(state) {
    this._previewState = state;
    this.refreshAllStyles();
  }

  /** Clear an active preview (see setPreviewState) — widgets immediately return to their normal resolved (non-preview) appearance. */
  clearPreviewState() {
    if (this._previewState == null) return;
    this._previewState = null;
    this.refreshAllStyles();
  }

  getProperties() {
    return this.properties;
  }

  /**
   *
   * @param {You may implement a redraw on every object} args
   */
  redraw(_args) { }

  /**
   * Called by refreshStyle for every color-type style property change. Only
   * main's bg_color/border_color get the special outer-rect fill/stroke
   * treatment — every other part's color is purely a data change that
   * widget-specific onStyle() handlers react to themselves. Layer/history
   * bookkeeping is refreshStyle's job, not this one's — this is a pure visual
   * side effect.
   */
  changeColor(part, prop, color) {
    if (part === 'main' && prop === 'bg_color') {
      const splitColors = splitColorOpa(color ?? '#000000ff');
      this.pokeStyle('main', 'bg_opa', Number(Math.round(splitColors.opacity * 255)));
      this.applyMainBackgroundStyle();
    } else if (part === 'main' && prop === 'border_color') {
      const splitColors = splitColorOpa(color ?? '#000000ff');
      this.pokeStyle('main', 'border_opa', Number(Math.round(splitColors.opacity * 255)));
      this.rect.setStroke(splitColors.color);
      this.rect.borderOpa(Math.round(splitColors.opacity * 255));
    } else if (part === 'main' && prop === 'outline_color') {
      // Mirror bg_color's handling: the picker's alpha channel is the single
      // source of truth for outline_opa, poked (not set) so it stays a derived
      // value that never lands in history as an independent edit.
      const splitColors = splitColorOpa(color ?? '#000000ff');
      this.pokeStyle('main', 'outline_opa', Number(Math.round(splitColors.opacity * 255)));
      this.applyOutlineStyle();
    }
  }

  /**
   * Apply the resolved Schema V2 background values to the shared rounded
   * CustomRect. The renderer receives only values from the normal style
   * cascade; Schema V2 metadata supplies the firmware enum-to-axis mapping.
   */
  applyMainBackgroundStyle() {
    this.applyBackgroundStyle(this.rect, 'main');
  }

  /**
   * Apply one resolved LVGL part background to a rectangle renderer.
   *
   * This is deliberately part-agnostic: `main`, `indicator` and `knob` all
   * use the same LVGL7 bg_color/bg_grad_color/bg_grad_dir/radius/border
   * pipeline. The rectangle itself owns the actual local gradient vector, so
   * a moving/resized knob never accidentally samples the parent widget box.
   */
  applyBackgroundStyle(rect, part = 'main') {
    if (!rect) return;

    const resolvedBackground = this.styleProp(part, 'bg_color');
    const background = resolvedBackground ?? (part === 'main' ? '#000000ff' : null);
    if (background == null) {
      rect.fill(null);
      rect.opacity(1);
      rect.setBackgroundGradient?.('none', '#000000', undefined, 0, 255);
      return;
    }

    const split = splitColorOpa(background);
    const hasEmbeddedOpacity = typeof background === 'string' && background.length > 7;
    const opacity = hasEmbeddedOpacity
      ? split.opacity
      : Number(this.styleProp(part, 'bg_opa') ?? 255) / 255;

    rect.fill(split.color);
    rect.opacity(opacity);
    const directionDefinition = this.getStylePropertyDefinition(part, 'bg_grad_dir');
    const direction = Number(this.styleProp(part, 'bg_grad_dir') ?? 0);
    rect.setBackgroundGradient?.(
      resolveGradientAxis(directionDefinition, direction),
      split.color,
      this.styleProp(part, 'bg_grad_color'),
      Number(this.styleProp(part, 'bg_main_stop') ?? 0),
      Number(this.styleProp(part, 'bg_grad_stop') ?? 255),
    );
  }

  getHassServices() {
    return this.hass_services;
  }

  getHassEvents() {
    return this.hass_events ?? [];
  }

  getServiceAction(service) {
    return servicesLookup[service];
  }

  /** Whether the object has Home Assistant configuration: stored, or in its page's flow graph. */
  hasHassConfig() {
    if (hasHassFlow(this)) return true;
    if (this.hass_config !== undefined) {
      return (
        (this.hass_config.automation || []).length > 0 ||
        (this.hass_config.events     || []).length > 0 ||
        (this.hass_config.properties || []).length > 0 ||
        (this.hass_config.chart_series || []).some(s => s.entity)
      );
    }
    return false;
  }

  //split konva colors in openhasp color and opacity
  addSplitColors(objectData, theme, colorProp, colorValue) {
    const splitColors = splitColorOpa(colorValue);
    if (splitColors.color !== theme[colorProp]) {
      objectData[colorProp] = splitColors.color;
    }
    //opacity for colors — carried by the colour's OWN opacity attribute
    //(bg_color -> bg_opa, bg_color10 -> bg_opa10, outline_color -> outline_opa),
    //derived by opaWireKey so the pairing lives in exactly one place. Only
    //properties declared `format: 'hex8'` can carry an alpha channel at all
    //(every other colour gets a plain 6-hex picker), so this branch is reached
    //by bg_color and outline_color.
    if (splitColors.opacity < 1) {
      objectData[opaWireKey(colorProp)] = Number(Math.round(splitColors.opacity * 255));
    }
  }

}
